/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import React, { useMemo } from 'react';
import { css } from 'aphrodite';
import { colors, LoadingSpinner, mixins } from '@blend/consumer-components';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { ConsumerAppConfigContext, useConsumerTheme } from '../../../../hooks/useConsumerTheme';
import { mediaBreakpoints, mediaOrientations } from '../../../../common/responsive';
import { MemoizedParticipant } from '../../Participant/Participant';
import { zIndex } from '../../../../style/zIndex';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { useVideoAudioStream } from '../hooks/useVideoAudioStream';
import { usePinnedParticipants } from '../hooks/usePinnedParticipants';
import { ClosedCaptions } from '@components/videoProvider/zoom/ClosedCaptions/ClosedCaptions';
import { useDominantSpeakerParticipant } from '@components/videoProvider/twilio/hooks/useDominantSpeakerParticipant';

const DESKTOP_HEIGHT = 156;
const MOBILE_HEIGHT = '13%';

const CAPTION_CONTAINER_DESKTOP_HEIGHT = 91;
const CAPTION_CONTAINER_MOBILE_HEIGHT = 56;
const generateStyles = (appConfig: ConsumerAppConfigContext) => ({
  stripWrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    ...mixins.paddingVertical(8),
    backgroundColor: appConfig.colors.blackPearl,
    [mediaBreakpoints.desktop]: {
      height: DESKTOP_HEIGHT,
    },
    [mediaBreakpoints.mobile]: {
      height: MOBILE_HEIGHT,
      ...mixins.paddingHorizontal(8),
      zIndex: zIndex.mobileParticipantStrip,
      [mediaOrientations.landscape]: {
        height: '100%',
        width: '25%',
        flexDirection: 'column',
      },
    },
  },
  stripWrapperWithCaptions: {
    flexDirection: 'column',
    [mediaBreakpoints.desktop]: {
      height: DESKTOP_HEIGHT + CAPTION_CONTAINER_DESKTOP_HEIGHT,
    },
    [mediaBreakpoints.mobile]: {
      height: `calc(${MOBILE_HEIGHT} + ${CAPTION_CONTAINER_MOBILE_HEIGHT}px)`,
    },
  },
  videoContainer: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',

    [mediaBreakpoints.mobile]: {
      [mediaOrientations.landscape]: {
        flexDirection: 'row',
      },
    },
  },
  videoTrackWrapper: {
    display: 'flex',
    [mediaBreakpoints.mobile]: {
      height: '100%', // this with videoPlayerContainer media: mobile are required so video-player keeps parent container height
      justifyContent: 'center',
      [mediaOrientations.landscape]: {
        flexDirection: 'column',
      },
    },
  },

  // All the styles below handle rendering the ParticipantStrip component as ParticipantTab
  stripWrapperMobileParticipantsView: {
    [mediaOrientations.portrait]: {
      height: '100%',
    },
    [mediaOrientations.landscape]: {
      width: '100%',
    },
  },
  videoContainerMobileParticipantsView: {
    alignItems: 'center',
    height: 'auto',
  },
  videoTrackWrapperMobileParticipantsView: {
    display: 'grid',
    alignItems: 'center',
    gridGap: 8,
    [mediaOrientations.portrait]: {
      ...mixins.marginHorizontal(15),
      gridTemplateColumns: 'repeat(auto-fill, minmax(40%, 1fr))',
    },
    [mediaOrientations.landscape]: {
      ...mixins.marginHorizontal(30),
      gridTemplateColumns: 'repeat(auto-fill, minmax(30%, 1fr))',
    },
  },
});
export namespace ZoomParticipantStrip {
  export interface Props {
    isParticipantsTabView?: boolean;
  }
}

const ZoomParticipantStrip: React.FC<ZoomParticipantStrip.Props> = ({ isParticipantsTabView }) => {
  const { isMobileView } = useRootSelector(state => state.layout);
  const { styles } = useConsumerTheme(generateStyles);
  const { users } = useRootSelector(state => ({
    users: state.signingRoom.users,
  }));

  const { videoProvider, closedCaptions } = useVideoContext();
  const { isLoading } = useVideoAudioStream();
  const { pinnedParticipants, nonPinnedParticipants } = usePinnedParticipants();
  const { dominantSpeakerParticipant } = useDominantSpeakerParticipant(nonPinnedParticipants);

  const participantsProps = useMemo(() => {
    // Local participant and notary participant will always be visible
    const pinnedParticipantsProps = pinnedParticipants.map(participant => ({
      participant,
      dataIt: 'Pinned-Participant',
    }));

    // Determine the participants to show based on view and dominant speaker
    const shouldShowDominantSpeaker = isMobileView && !isParticipantsTabView && !!dominantSpeakerParticipant;
    const dominantSpeakerParticipants = shouldShowDominantSpeaker ? [dominantSpeakerParticipant] : [];

    //  Show all participant on desktop, but only show one non-pinned dominant speaker participant on mobile
    const nonPinnedParticipantsProps = (
      isMobileView && !isParticipantsTabView ? dominantSpeakerParticipants : nonPinnedParticipants
    ).map(participant => ({
      participant,
      dataIt: shouldShowDominantSpeaker ? 'Dominant-Speaker-Participant' : 'Nonpinned-Participant',
    }));
    return [...pinnedParticipantsProps, ...nonPinnedParticipantsProps];
  }, [pinnedParticipants, dominantSpeakerParticipant, nonPinnedParticipants, isMobileView, isParticipantsTabView]);

  if (isLoading) {
    return (
      <div className={css(styles.stripWrapper)} data-it="ParticipantStrip">
        <LoadingSpinner size="sm" color={colors.catalyst} />
      </div>
    );
  }

  /**
   * TODO: (ZOOMSDK)
   * Firefox has a known limitation in zoom videoSdk
   * They suggest we do multiple container per each video player instead of the recommended
   * one video container with multiple video-players.
   * Doing multiple container per each video player also works with chrome but that's not the best way according to zoom.
   * https://devforum.zoom.us/t/firefox-individual-elements-not-working-with-multiple-videos/106655/2
   */
  const canSupportMultipleVideos = videoProvider.isSupportMultipleVideos();
  return (
    <div
      data-it="ParticipantStrip"
      className={css(
        styles.stripWrapper,
        closedCaptions.isEnabled && styles.stripWrapperWithCaptions,
        isParticipantsTabView && styles.stripWrapperMobileParticipantsView,
      )}
    >
      <div className={css(styles.videoContainer, isParticipantsTabView && styles.videoContainerMobileParticipantsView)}>
        {canSupportMultipleVideos ? (
          <video-player-container>
            <div
              className={css(
                styles.videoTrackWrapper,
                isParticipantsTabView && styles.videoTrackWrapperMobileParticipantsView,
              )}
            >
              {participantsProps.map(participantProps => (
                <MemoizedParticipant
                  key={participantProps.participant.userId}
                  participant={participantProps.participant}
                  users={users}
                  isParticipantsTabView={isParticipantsTabView}
                />
              ))}
            </div>
          </video-player-container>
        ) : (
          participantsProps.map(participantProps => (
            <div key={participantProps.participant.userId}>
              <video-player-container>
                <MemoizedParticipant
                  participant={participantProps.participant}
                  users={users}
                  isParticipantsTabView={isParticipantsTabView}
                />
              </video-player-container>
            </div>
          ))
        )}
      </div>
      {closedCaptions.isEnabled ? <ClosedCaptions isParticipantsTabView={isParticipantsTabView} /> : null}
    </div>
  );
};

export const MemoizedZoomParticipantStrip = React.memo(ZoomParticipantStrip);
